function getFieldControls(field) {
    return field.querySelectorAll(":is(input, select, textarea)[name]");
}

function getOwnValidationMessage(field) {
    return field.querySelector(":scope > .error [data-valmsg-for]");
}

function getNestedValidationMessages(field) {
    return field.querySelectorAll(
        ":scope > .control [data-form-field] > .error [data-valmsg-for].field-validation-error"
    );
}

function isFieldTouched(field) {
    return field?.dataset.touched === "true";
}

function markFieldTouched(field) {
    if (!field) return;
    field.dataset.touched = "true";
}

function markFieldChainTouched(el) {
    getFieldChain(el).forEach(markFieldTouched);
}

function markFormFieldsTouched(form) {
    form.querySelectorAll("[data-form-field]").forEach(markFieldTouched);
}

function isControlInvalid(control) {
    return (
        control.classList.contains("input-validation-error") ||
        control.getAttribute("aria-invalid") === "true"
    );
}

function getRequiredControls(field) {
    return Array.from(
        field.querySelectorAll(":is(input, select, textarea)[data-val-required]")
    );
}

function groupControlsByName(controls) {
    return controls.reduce((groups, control) => {
        if (!control.name) return groups;

        if (!groups[control.name]) {
            groups[control.name] = [];
        }

        groups[control.name].push(control);
        return groups;
    }, {});
}

function hasControlValue(control) {
    if (control.disabled) return true;

    const type = (control.getAttribute("type") || "").toLowerCase();

    if (type === "checkbox" || type === "radio") {
        return control.checked;
    }

    return Boolean(String(control.value || "").trim());
}

function getCheckboxGroupMessage(field) {
    if (field.dataset.groupRequired !== "true") {
        return null;
    }

    const choices = Array.from(
        field.querySelectorAll('input[type="checkbox"], input[type="radio"]')
    );

    const hasSelection = choices.some((choice) => choice.checked);

    if (hasSelection) {
        return null;
    }

    return field.dataset.groupMessage || null;
}

function getRequiredGroupMessage(field) {
    if (!field.matches("[data-form-field-group]")) return null;

    if (hasUploadReferences(field)) {
        return null;
    }

    const requiredControls = getRequiredControls(field)
        .filter((control) => !control.matches("[data-upload-input]"));

    if (!requiredControls.length) return null;

    const groups = groupControlsByName(requiredControls);

    for (const controls of Object.values(groups)) {
        const first = controls[0];
        const type = (first.getAttribute("type") || "").toLowerCase();

        const hasValue =
            type === "radio" || type === "checkbox"
                ? controls.some((control) => control.checked)
                : controls.some(hasControlValue);

        if (!hasValue) {
            return first.getAttribute("data-val-required");
        }
    }

    return null;
}

function hasUploadReferences(field) {
    return Array.from(
        field.querySelectorAll("[data-upload-hidden]")
    ).some((input) => Boolean(input.name && String(input.value || "").trim()));
}

function setValidationMessage(messageNode, message, source = "client") {
    if (!messageNode) return;

    const hasMessage = Boolean(message && message.trim());

    messageNode.textContent = hasMessage ? message : "";
    messageNode.classList.toggle("field-validation-error", hasMessage);
    messageNode.classList.toggle("field-validation-valid", !hasMessage);

    if (hasMessage && source === "client") {
        messageNode.dataset.formFieldMessageSource = "client";
    } else {
        delete messageNode.dataset.formFieldMessageSource;
    }
}

function getOwnServerMessage(field) {
    const messageNode = getOwnValidationMessage(field);
    if (!messageNode) return null;

    if (messageNode.dataset.formFieldMessageSource === "client") {
        return null;
    }

    const hasServerError =
        messageNode.classList.contains("field-validation-error") &&
        Boolean(messageNode.textContent.trim());

    return hasServerError ? messageNode.textContent.trim() : null;
}

function getOwnClientMessage(field) {
    const messageNode = getOwnValidationMessage(field);
    if (!messageNode) return null;

    if (messageNode.dataset.formFieldMessageSource !== "client") {
        return null;
    }

    const hasClientError =
        messageNode.classList.contains("field-validation-error") &&
        Boolean(messageNode.textContent.trim());

    return hasClientError ? messageNode.textContent.trim() : null;
}

function hasInvalidUploadControl(field) {
    return Boolean(
        field.querySelector(
            "[data-upload-input].input-validation-error, [data-upload-input][aria-invalid='true']"
        )
    );
}

function getFirstNestedErrorMessage(field) {
    const messageNode = Array.from(getNestedValidationMessages(field))
        .find((node) => node.textContent.trim());

    return messageNode?.textContent.trim() || null;
}

function syncGroupField(field) {
    const ownMessageNode = getOwnValidationMessage(field);

    const serverMessage = getOwnServerMessage(field);

    const requiredMessage = isFieldTouched(field)
        ? getRequiredGroupMessage(field)
        : null;

    const checkboxGroupMessage = isFieldTouched(field)
        ? getCheckboxGroupMessage(field)
        : null;

    const uploadClientMessage = hasInvalidUploadControl(field)
        ? getOwnClientMessage(field)
        : null;

    const nestedMessage = getFirstNestedErrorMessage(field);

    const groupMessage =
        serverMessage ||
        requiredMessage ||
        checkboxGroupMessage ||
        uploadClientMessage ||
        nestedMessage;

    const source = serverMessage ? "server" : "client";

    setValidationMessage(ownMessageNode, groupMessage, source);
    field.classList.toggle("is-invalid", Boolean(groupMessage));
}

function syncLeafField(field) {
    const ownMessageNode = getOwnValidationMessage(field);
    const controls = Array.from(getFieldControls(field));
    const invalid = controls.some(isControlInvalid);

    if (field.dataset.formFieldErrorSuppressed === "true") {
        field.classList.toggle("is-invalid", invalid);
        return;
    }

    const ownMessageInvalid =
        ownMessageNode?.classList.contains("field-validation-error") === true &&
        Boolean(ownMessageNode.textContent.trim());

    field.classList.toggle("is-invalid", invalid || ownMessageInvalid);
}

function syncField(field) {
    if (!field) return;

    if (field.matches("[data-form-field-group]")) {
        syncGroupField(field);
        return;
    }

    syncLeafField(field);
}

function getFieldChain(el) {
    const fields = [];
    let current = el?.closest?.("[data-form-field]");

    while (current) {
        fields.push(current);
        current = current.parentElement?.closest?.("[data-form-field]");
    }

    return fields;
}

function scheduleSync(field) {
    if (!field) return;
    if (field.__syncQueued) return;

    field.__syncQueued = true;

    requestAnimationFrame(() => {
        field.__syncQueued = false;
        syncField(field);
    });
}

function syncForm(form) {
    if (!(form instanceof HTMLFormElement)) return;

    form.querySelectorAll("[data-form-field]").forEach(syncField);
}

function syncFormSoon(form) {
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            syncForm(form);
        });
    });
}

function getPasswordToggleInput(toggle) {
    const frame = toggle.closest("[data-form-field-input-frame]");
    if (!frame) return null;

    const input = frame.querySelector(
        'input[type="password"], input[type="text"][data-form-field-password-revealed="true"]'
    );

    return input instanceof HTMLInputElement ? input : null;
}

function getPasswordToggleIcon(toggle) {
    return toggle.querySelector("sds-icon");
}

function setPasswordToggleIcon(toggle, revealed) {
    const icon = getPasswordToggleIcon(toggle);
    if (!icon) return;

    icon.setAttribute("name", revealed ? "eye-off" : "eye");
}

function setPasswordToggleLabel(toggle, revealed) {
    toggle.setAttribute(
        "aria-label",
        revealed ? "Wachtwoord verbergen" : "Wachtwoord tonen"
    );
}

function setPasswordVisibility(toggle, revealed) {
    const input = getPasswordToggleInput(toggle);
    if (!input) return;

    input.type = revealed ? "text" : "password";

    if (revealed) {
        input.dataset.formFieldPasswordRevealed = "true";
    } else {
        delete input.dataset.formFieldPasswordRevealed;
    }

    toggle.setAttribute("aria-pressed", revealed ? "true" : "false");
    setPasswordToggleIcon(toggle, revealed);
    setPasswordToggleLabel(toggle, revealed);
}

function togglePasswordVisibility(toggle) {
    const input = getPasswordToggleInput(toggle);
    if (!input) return;

    const revealed = input.type === "password";

    setPasswordVisibility(toggle, revealed);
    input.focus({ preventScroll: true });
}

function resetPasswordToggles(form) {
    form.querySelectorAll("[data-form-field-password-toggle]").forEach((toggle) => {
        setPasswordVisibility(toggle, false);
    });
}

function bindFieldObservers(root) {
    root.querySelectorAll("[data-form-field]").forEach((field) => {
        if (field.__observerBound) return;

        const messageNodes = field.querySelectorAll("[data-valmsg-for]");

        const observer = new MutationObserver(() => {
            getFieldChain(field).forEach(scheduleSync);
        });

        messageNodes.forEach((node) => {
            observer.observe(node, {
                attributes: true,
                attributeFilter: ["class"],
                childList: true,
                characterData: true,
                subtree: true,
            });
        });

        field.__observerBound = true;
        field.__observer = observer;
    });
}

export function initFormFields(root = document) {
    bindFieldObservers(root);
    root.querySelectorAll?.("[data-form-field]").forEach(syncField);

    if (document.__formFieldsBound === true) return;

    ["input", "change", "blur"].forEach((eventName) => {
        document.addEventListener(
            eventName,
            (event) => {
                markFieldChainTouched(event.target);

                getFieldChain(event.target).forEach((field) => {
                    scheduleSync(field);
                });
            },
            true
        );
    });

    document.addEventListener(
        "click",
        (event) => {
            const toggle = event.target.closest?.("[data-form-field-password-toggle]");
            if (!toggle) return;

            event.preventDefault();
            togglePasswordVisibility(toggle);
        },
        true
    );

    document.addEventListener(
        "click",
        (event) => {
            const icon = event.target.closest?.("[data-form-field-post-icon]");
            if (!icon) return;

            if (icon.matches("button") || icon.closest("[data-form-field-password-toggle]")) return;

            const select = icon.parentElement?.querySelector(":scope > select");
            if (!select || select.disabled) return;

            event.preventDefault();
            select.focus({ preventScroll: true });

            if (typeof select.showPicker === "function") {
                try {
                    select.showPicker();
                } catch {
                    // ignore
                }
            }
        },
        true
    );

    document.addEventListener(
        "submit",
        (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) return;

            markFormFieldsTouched(form);
            syncFormSoon(form);
        },
        true
    );

    document.addEventListener(
        "sds:form:validated",
        (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) return;

            markFormFieldsTouched(form);
            syncFormSoon(form);
        },
        true
    );

    document.addEventListener(
        "reset",
        (event) => {
            const form = event.target;
            if (!(form instanceof HTMLFormElement)) return;

            form.querySelectorAll("[data-form-field]").forEach((field) => {
                delete field.dataset.touched;
            });

            resetPasswordToggles(form);

            requestAnimationFrame(() => {
                syncForm(form);
            });
        },
        true
    );

    document.__formFieldsBound = true;
}